import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@iplan/auth/react';
import { bearerTokenCache } from '@iplan/auth';
import { configureApiClient } from '@iplan/api-client';
import { authClient } from './lib/auth';
import { getEnv } from './lib/env';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { LocalisationProvider } from './contexts/LocalisationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import './styles.css';

const apiBaseUrl = getEnv('VITE_API_BASE_URL') ?? '/api';

// Configure the generated @iplan/api-client mutator (REST hooks) to hit the
// backend and attach the current access token.
configureApiClient({
  baseURL: apiBaseUrl,
  getAccessToken: () => authClient.getAccessToken(),
});

// Bridge the auth session into the bearer-token cache that lib/axios (OData +
// inlined page calls) reads synchronously. Keep it in sync on every change.
void authClient.getAccessToken().then((t) => bearerTokenCache.setToken(t));
authClient.onUserChanged((user) => bearerTokenCache.setToken(user?.access_token ?? null));

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('apps/web: #root element missing');
}

createRoot(rootElement).render(
  <StrictMode>
    <AuthProvider client={authClient}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LocalisationProvider>
            <PermissionsProvider>
              <Suspense
                fallback={
                  <div className="flex min-h-screen items-center justify-center bg-iplan-surface-muted text-iplan-text-muted">
                    Loading…
                  </div>
                }
              >
                <RouterProvider router={router} />
              </Suspense>
              <Toaster position="bottom-right" richColors closeButton />
            </PermissionsProvider>
          </LocalisationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
